import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const provider = params.get("provider");
  const mpPaymentId = params.get("payment_id");
  const legacySessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState(
    provider === "mp" && mpPaymentId
      ? "checking"
      : legacySessionId
        ? "checking"
        : "missing"
  );
  const clearedRef = useRef(false);

  const succeed = () => {
    setState("paid");
    if (!clearedRef.current) {
      clearedRef.current = true;
      clear();
    }
  };

  useEffect(() => {
    if (provider === "mp" && mpPaymentId) {
      let tries = 0;
      let stop = false;
      const tick = async () => {
        if (stop) return;
        let data = null;
        try {
          const res = await api.get(`/mp/payment/${mpPaymentId}`);
          data = res.data;
        } catch {
          data = null;
        }
        if (data?.payment_status === "paid") {
          succeed();
          return;
        }
        if (data?.payment_status === "failed") {
          setState("error");
          return;
        }
        tries += 1;
        if (tries < 8) setTimeout(tick, 2500);
        else setState("pending");
      };
      tick();
      return () => {
        stop = true;
      };
    }
    if (legacySessionId) {
      let tries = 0;
      let stop = false;
      const tick = async () => {
        if (stop) return;
        try {
          const { data } = await api.get(`/payments/status/${legacySessionId}`);
          if (data.payment_status === "paid") {
            succeed();
            return;
          }
        } catch {
          setState("error");
          return;
        }
        tries += 1;
        if (tries < 10) setTimeout(tick, 2000);
        else setState("pending");
      };
      tick();
      return () => {
        stop = true;
      };
    }
  }, [provider, mpPaymentId, legacySessionId]);

  const configs = {
    paid: {
      icon: <CheckCircle2 size={56} className="text-cyan" strokeWidth={1.4} />,
      title: "Pagamento aprovado!",
      copy: "Obrigado pela compra. Você receberá a confirmação do Mercado Pago por e-mail e o pedido entra em preparação para envio.",
      testid: "payment-success-paid",
    },
    checking: {
      icon: <Loader2 size={56} className="text-cyan animate-spin" strokeWidth={1.4} />,
      title: "Confirmando pagamento...",
      copy: "Só um instante: estamos verificando com o Mercado Pago.",
      testid: "payment-success-checking",
    },
    pending: {
      icon: <AlertTriangle size={56} className="text-amber-300" strokeWidth={1.4} />,
      title: "Pagamento em processamento",
      copy: "O Mercado Pago ainda está processando o pagamento (comum no Pix e boleto). A confirmação chega automaticamente e você receberá um e-mail.",
      testid: "payment-success-pending",
    },
    error: {
      icon: <XCircle size={56} className="text-destructive" strokeWidth={1.4} />,
      title: "Pagamento não aprovado",
      copy: "Se o valor foi cobrado, o estorno é automático pelo Mercado Pago. Você pode tentar novamente com outra forma de pagamento.",
      testid: "payment-success-error",
    },
    missing: {
      icon: <AlertTriangle size={56} className="text-amber-300" strokeWidth={1.4} />,
      title: "Sessão não encontrada",
      copy: "Abra o link de confirmação direto da tela de checkout.",
      testid: "payment-success-missing",
    },
  };
  const c = configs[state];

  return (
    <div className="bg-ink min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md" data-testid={c.testid}>
        <div className="flex justify-center">{c.icon}</div>
        <h1 className="font-display font-semibold text-4xl mt-7">{c.title}</h1>
        <p className="text-mute mt-4 leading-relaxed">{c.copy}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-9">
          <Link
            to="/loja"
            className="inline-flex h-12 items-center rounded-md bg-cyan px-7 font-semibold text-ink transition-all hover:bg-cyan-dark hover:-translate-y-0.5"
            data-testid="payment-success-back"
          >
            Continuar comprando
          </Link>
          <Link
            to="/"
            className="inline-flex h-12 items-center rounded-md border border-white/20 px-7 text-white transition-all hover:border-cyan hover:text-cyan"
          >
            Início
          </Link>
        </div>
      </div>
    </div>
  );
}
